import { ColumnDef } from '@tanstack/react-table';
import { SupplierStatus } from './SupplierStatus';
import { Checkbox } from '@/components/ui/checkbox';
import SupplierStatusCell from '@/components/suppliers/SupplierStatusCell';
import { format } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { Translator } from '../translator.type';

export type Supplier = {
  id: string;
  code: number;
  name: string;
  country: string;
  city: string;
  status: SupplierStatus;
  updatedAt: string;
};

export const supplierColumns = (t: Translator): ColumnDef<Supplier>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'code',
    header: t('suppliers.columns.code'),
    cell: ({ row }) =>
      row.original.code && (
        <span>{`F${row.original.code.toString().padStart(4, '0')}`}</span>
      ),
  },
  {
    accessorKey: 'name',
    header: t('suppliers.columns.name'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.name}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'country',
    header: t('suppliers.columns.country'),
  },
  {
    accessorKey: 'city',
    header: t('suppliers.columns.city'),
  },
  {
    accessorKey: 'status',
    header: t('suppliers.columns.status'),
    cell: ({ row }) => <SupplierStatusCell status={row.original.status} />,
  },
  {
    accessorKey: 'updatedAt',
    header: t('suppliers.columns.updatedAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
];
